import { Download, FolderInput, Tag, Trash2 } from "lucide-react";
import { useState } from "react";
import type { ResponsiveNotePhotoLabel } from "@rebuild/shared/models";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PhotoLabelPicker } from "./PhotoLabelPicker";
import { PhotoMovePanel, type RoomOption } from "./PhotoMovePanel";
import { PhotoToolbarAction } from "./PhotoToolbarAction";

type ToolbarPanel = "move" | "labels";

interface PhotoSelectionToolbarProps {
  estimateId: string | undefined;
  selectedCount: number;
  roomOptions: RoomOption[];
  /** Room every selected photo already belongs to, if they all share one. */
  currentRoomId?: string;
  onMoveToRoom: (roomId: string, roomName: string) => void;
  onClear: () => void;
  onDownload?: () => void;
  isDownloadPending?: boolean;
  onDelete?: () => void;
  isDeletePending?: boolean;
  isDeleteDisabled?: boolean;
  isMoveDisabled?: boolean;
  isMovePending?: boolean;
  onApplyLabels: (labels: ResponsiveNotePhotoLabel[]) => void;
  isLabelPending?: boolean;
  /** Explains why labelling is unavailable for the current selection; also disables the picker. */
  labelDisabledReason?: string;
}

export function PhotoSelectionToolbar({
  estimateId,
  selectedCount,
  roomOptions,
  currentRoomId,
  onMoveToRoom,
  onClear,
  onDownload,
  isDownloadPending = false,
  onDelete,
  isDeletePending = false,
  isDeleteDisabled = false,
  isMoveDisabled = false,
  isMovePending = false,
  onApplyLabels,
  isLabelPending = false,
  labelDisabledReason,
}: PhotoSelectionToolbarProps) {
  const [openPanel, setOpenPanel] = useState<ToolbarPanel | null>(null);

  const isVisible = selectedCount > 0;

  // Closing on an empty selection keeps staged chips and search text from leaking into the next one.
  if (!isVisible && openPanel !== null) {
    setOpenPanel(null);
  }

  const togglePanel = (panel: ToolbarPanel) => setOpenPanel((current) => (current === panel ? null : panel));
  const closePanel = () => setOpenPanel(null);

  const handleMoveToRoom = (room: RoomOption) => {
    closePanel();
    onMoveToRoom(room.value, room.label);
  };

  const handleDownload = () => {
    closePanel();
    onDownload?.();
  };

  const handleDelete = () => {
    closePanel();
    onDelete?.();
  };

  return (
    <div
      className={cn(
        "fixed bottom-4 left-1/2 z-50 flex w-[min(26rem,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-2 rounded-xl border border-gray-950/10 bg-gray-900 px-3 py-2 shadow-2xl transition-all duration-200",
        isVisible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0",
      )}
    >
      {/* Unmounted when nothing is selected: keeps the hidden toolbar out of the tab order. */}
      {isVisible && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-white">{selectedCount} selected</span>
            {/* The visible count is frozen during the exit animation, so announce the real one separately. */}
            <span className="sr-only" aria-live="polite">
              {selectedCount} selected
            </span>
            <Button variant="ghost" size="xs" className="text-indigo-300 hover:bg-gray-800 hover:text-indigo-200" onClick={onClear}>
              Clear
            </Button>
          </div>
          <div className="border-t border-gray-800" />
          {openPanel === "move" && (
            <PhotoMovePanel
              photoCount={selectedCount}
              roomOptions={roomOptions}
              currentRoomId={currentRoomId}
              isPending={isMovePending}
              onSelectRoom={handleMoveToRoom}
              onClose={closePanel}
            />
          )}
          {openPanel === "labels" && (
            <PhotoLabelPicker
              estimateId={estimateId}
              photoCount={selectedCount}
              onClose={closePanel}
              onApply={onApplyLabels}
              isPending={isLabelPending}
              disabledReason={labelDisabledReason}
            />
          )}
          {openPanel !== null && <div className="border-t border-gray-800" />}
          <div className="grid grid-cols-4 gap-1">
            <PhotoToolbarAction
              icon={FolderInput}
              label="Move"
              controls="photo-toolbar-move-panel"
              isActive={openPanel === "move"}
              isDisabled={isMoveDisabled}
              isPending={isMovePending}
              onClick={() => togglePanel("move")}
            />
            <PhotoToolbarAction
              icon={Tag}
              label="Labels"
              controls="photo-toolbar-labels-panel"
              isActive={openPanel === "labels"}
              isPending={isLabelPending}
              onClick={() => togglePanel("labels")}
            />
            <PhotoToolbarAction
              icon={Download}
              label="Download"
              isDisabled={isDownloadPending}
              isPending={isDownloadPending}
              onClick={handleDownload}
            />
            <PhotoToolbarAction
              icon={Trash2}
              label="Delete"
              isDestructive
              isDisabled={isDeleteDisabled}
              isPending={isDeletePending}
              onClick={handleDelete}
            />
          </div>
        </>
      )}
    </div>
  );
}
